import type { ServerSideRequest, TableColumns, ValueLists } from '@ohpen/enso-ui';

// Translates what an enso server-side table asks for (column filters, global search, sort, page) into the body of
// a structured query endpoint (SQE). Pure, so the list hooks stay thin and the rules can be read in one place.

export type SqeNode =
  | { type: 'group'; op: 'and' | 'or' | 'not'; nodes: SqeNode[] }
  | { type: 'predicate'; field: string; op: string; value?: unknown }
  | { type: 'collection'; collection: string; op: 'any' | 'all' | 'none'; node: SqeNode };

export type SqeQuery = {
  filter?: SqeNode;
  sort?: { field: string; dir: 'asc' | 'desc' }[];
  page: number;
  pageSize: number;
};

export type SqePage<T> = { items: T[]; total: number };

/**
 * How the SQE declares a member, which decides the operators a column filter can be sent with.
 * - `text`: eq, contains.
 * - `identifier`: a 32-bit number searched by its digits (the FRN): contains, eq.
 * - `enum`: in over the value-list keys.
 * - `boolean`: eq over `TRUE`/`FALSE` value-list keys.
 * - `reference`: a `{ key, value }` member, in over the keys and contains over the names.
 * - `date`: ranges.
 * - `number`: ranges.
 * - `set`: an array of value-list keys: anyOf, subsetOf, exactSet, isEmpty.
 *
 * Each adds isNull when `nullable`.
 */
export type SqeField = {
  kind: 'text' | 'identifier' | 'enum' | 'boolean' | 'reference' | 'date' | 'number' | 'set';
  nullable?: boolean;
};

/** A collection member: `key` is the element field a select filter compares, `search` the one a global search reads. */
export type SqeCollectionField = { kind: 'collection'; key: string; search?: string };

type PredicateFieldOf<N> = N extends { type: 'predicate'; field: infer F } ? F : never;
type NullableFieldOf<N> = N extends { type: 'predicate'; op: 'isNull' | 'isNotNull'; field: infer F } ? F : never;
type CollectionOf<N> = N extends { type: 'collection'; collection: infer C } ? C : never;

/**
 * The members of an SQE's filter node a list maps its columns to, keyed by the column's `dataIndex`. `nullable` is
 * required exactly where the SQE accepts isNull, so the map cannot drift from the generated schema.
 */
export type SqeFields<N> = {
  [F in PredicateFieldOf<N> & string]?: SqeField & (F extends NullableFieldOf<N> ? { nullable: true } : { nullable?: false });
} & {
  [C in CollectionOf<N> & string]?: SqeCollectionField;
};

export type SqeSort = NonNullable<SqeQuery['sort']>;

type AnySqeField = SqeField | SqeCollectionField;

type ColumnFilterEntry = { search?: string; select?: string; number?: string; value?: unknown };

type ColumnInfo = { columnKey: string; dataIndex: string; filter?: string; listName?: string };

export const SQE_MAX_PAGE_SIZE = 200;

const SQE_MAX_SORT_TERMS = 3;

const SQE_MAX_LIST_VALUES = 100;

const SQE_MAX_IDENTIFIER = 2_147_483_647;

const DEFAULT_PAGE_SIZE = 20;

const columnInfos = <T>(columns: TableColumns<T>): ColumnInfo[] =>
  columns.flatMap(column => {
    const { key, dataIndex, options } = column as {
      key?: unknown;
      dataIndex?: unknown;
      options?: { filter?: string; listName?: string };
    };
    if (typeof dataIndex !== 'string' || !dataIndex) return [];
    return [{ columnKey: String(key ?? dataIndex), dataIndex, filter: options?.filter, listName: options?.listName }];
  });

const predicate = (field: string, op: string, value?: unknown): SqeNode =>
  value === undefined ? { type: 'predicate', field, op } : { type: 'predicate', field, op, value };

const group = (op: 'and' | 'or', nodes: SqeNode[]): SqeNode | undefined => {
  if (nodes.length === 0) return undefined;
  return nodes.length === 1 ? nodes[0] : { type: 'group', op, nodes };
};

const collection = (name: string, op: 'any' | 'all', node: SqeNode): SqeNode => ({ type: 'collection', collection: name, op, node });

// One `in` per slice of keys the SQE takes in a list, joined with OR, so a long selection is not refused.
const inKeys = (field: string, keys: string[]): SqeNode => {
  const slices: SqeNode[] = [];
  for (let start = 0; start < keys.length; start += SQE_MAX_LIST_VALUES) {
    slices.push(predicate(field, 'in', keys.slice(start, start + SQE_MAX_LIST_VALUES)));
  }
  return group('or', slices) as SqeNode;
};

const isDigits = (text: string): boolean => /^\d+$/.test(text);

const isIdentifier = (text: string): boolean => isDigits(text) && Number(text) <= SQE_MAX_IDENTIFIER;

// A day picked in a date filter, as the instant it starts at in the browser's time zone, the one the grid shows
// dates in, moved by `offset` days.
const dayStart = (day: string, offset = 0): string | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!match) return undefined;
  const [, year, month, date] = match.map(Number);
  return new Date(year, month - 1, date + offset).toISOString();
};

const textFilter = (field: string, spec: AnySqeField, mode: string, value: unknown): SqeNode | undefined => {
  if (mode === 'empty') return predicate(field, 'isNull');
  const text = (Array.isArray(value) ? value[0] : value)?.toString().trim();
  if (!text) return undefined;
  if (mode === 'exact') {
    if (spec.kind !== 'identifier') return predicate(field, 'eq', text);
    // Anything but digits that fit a 32-bit integer matches no FRN; contains says so without an integer the SQE would refuse.
    return isIdentifier(text) ? predicate(field, 'eq', Number(text)) : predicate(field, 'contains', text);
  }
  return predicate(field, 'contains', text);
};

const selectFilter = (field: string, spec: AnySqeField, mode: string, value: unknown): SqeNode | undefined => {
  if (mode === 'empty') return predicate(field, spec.kind === 'set' ? 'isEmpty' : 'isNull');
  const keys = [...new Set((Array.isArray(value) ? value.flat() : [value]).filter(key => key != null).map(String))];
  if (keys.length === 0) return undefined;

  if (spec.kind === 'set') {
    if (mode === 'allMatches') return predicate(field, 'subsetOf', keys);
    if (mode === 'exactMatch') return predicate(field, 'exactSet', keys);
    return predicate(field, 'anyOf', keys);
  }
  if (spec.kind === 'boolean') {
    // Both Yes and No selected is no restriction at all.
    return keys.length === 1 ? predicate(field, 'eq', keys[0] === 'TRUE') : undefined;
  }
  if (spec.kind === 'collection') {
    const within = inKeys(spec.key, keys);
    if (mode === 'allMatches') return collection(field, 'all', within);
    if (mode === 'exactMatch') {
      return group('and', [collection(field, 'all', within), ...keys.map(key => collection(field, 'any', predicate(spec.key, 'eq', key)))]);
    }
    return collection(field, 'any', within);
  }
  return inKeys(field, keys);
};

const rangeFilter = (field: string, spec: AnySqeField, mode: string, value: unknown): SqeNode | undefined => {
  if (mode === 'empty') return predicate(field, 'isNull');
  const [exact, min, max] = (Array.isArray(value) ? (Array.isArray(value[0]) ? value[0] : value) : []) as unknown[];

  if (spec.kind === 'date') {
    const day = (raw: unknown, offset = 0) => (typeof raw === 'string' ? dayStart(raw, offset) : undefined);
    const bounds: [string, string | undefined][] =
      mode === 'greater'
        ? [['gte', day(exact, 1)]]
        : mode === 'less'
          ? [['lt', day(exact)]]
          : mode === 'range'
            ? [
                ['gte', day(min)],
                ['lt', day(max, 1)],
              ]
            : [
                ['gte', day(exact)],
                ['lt', day(exact, 1)],
              ];
    return group(
      'and',
      bounds.flatMap(([op, instant]) => (instant ? [predicate(field, op, instant)] : [])),
    );
  }

  const number = (raw: unknown) => (raw === null || raw === undefined || raw === '' ? undefined : Number(raw));
  const bounds: [string, number | undefined][] =
    mode === 'greater'
      ? [['gt', number(exact)]]
      : mode === 'less'
        ? [['lt', number(exact)]]
        : mode === 'range'
          ? [
              ['gte', number(min)],
              ['lte', number(max)],
            ]
          : [['eq', number(exact)]];
  return group(
    'and',
    bounds.flatMap(([op, bound]) => (bound === undefined || Number.isNaN(bound) ? [] : [predicate(field, op, bound)])),
  );
};

type FilterMode = 'contains' | 'exact' | 'empty' | 'anyMatches' | 'allMatches' | 'exactMatch' | 'greater' | 'less' | 'range';

const modesFor = (spec: AnySqeField, filter: string | undefined): FilterMode[] | undefined => {
  const empty: FilterMode[] = spec.kind !== 'collection' && spec.nullable ? ['empty'] : [];
  switch (filter) {
    case 'search':
      return ['contains', 'exact', ...empty];
    case 'select':
      if (spec.kind === 'collection') return ['anyMatches', 'allMatches', 'exactMatch'];
      if (spec.kind === 'set') return ['anyMatches', 'allMatches', 'exactMatch', 'empty'];
      return ['anyMatches', ...empty];
    case 'number':
    case 'date':
      return ['exact', 'greater', 'less', 'range', ...empty];
    default:
      return undefined;
  }
};

const modeOf = (filter: string | undefined, entry: ColumnFilterEntry): string | undefined => {
  switch (filter) {
    case 'search':
      return entry.search ?? 'contains';
    case 'select':
      return entry.select ?? 'anyMatches';
    case 'number':
    case 'date':
      return entry.number ?? 'exact';
    default:
      return undefined;
  }
};

// A mode the member cannot be queried with (one restored from a filter saved before the column offered fewer) is
// left out rather than sent for the SQE to refuse.
const columnFilter = (info: ColumnInfo, spec: AnySqeField, entry: ColumnFilterEntry): SqeNode | undefined => {
  if (entry.value == null) return undefined;
  const mode = modeOf(info.filter, entry);
  if (!mode || !modesFor(spec, info.filter)?.includes(mode as FilterMode)) return undefined;
  switch (info.filter) {
    case 'search':
      return textFilter(info.dataIndex, spec, mode, entry.value);
    case 'select':
      return selectFilter(info.dataIndex, spec, mode, entry.value);
    default:
      return rangeFilter(info.dataIndex, spec, mode, entry.value);
  }
};

// The value-list keys whose shown label contains the search, so a search finds what the cells display.
const keysLabelled = (listName: string | undefined, valueLists: ValueLists | undefined, search: string): string[] => {
  const list = listName ? (valueLists?.[listName] as Record<string, string> | undefined) : undefined;
  if (!list) return [];
  const lower = search.toLowerCase();
  return Object.entries(list)
    .filter(([, label]) => String(label).toLowerCase().includes(lower))
    .map(([key]) => key);
};

const globalSearch = (info: ColumnInfo, spec: AnySqeField, search: string, valueLists: ValueLists | undefined): SqeNode | undefined => {
  switch (spec.kind) {
    case 'text':
    case 'reference':
      return predicate(info.dataIndex, 'contains', search);
    case 'identifier':
      return isDigits(search) ? predicate(info.dataIndex, 'contains', search) : undefined;
    case 'enum': {
      const keys = keysLabelled(info.listName, valueLists, search);
      return keys.length ? inKeys(info.dataIndex, keys) : undefined;
    }
    case 'set': {
      const keys = keysLabelled(info.listName, valueLists, search);
      return keys.length ? predicate(info.dataIndex, 'anyOf', keys) : undefined;
    }
    case 'collection': {
      if (spec.search) return collection(info.dataIndex, 'any', predicate(spec.search, 'contains', search));
      const keys = keysLabelled(info.listName, valueLists, search);
      return keys.length ? collection(info.dataIndex, 'any', inKeys(spec.key, keys)) : undefined;
    }
    default:
      return undefined;
  }
};

type SorterEntry = { columnKey?: unknown; field?: unknown; order?: 'ascend' | 'descend' | null };

const sortTerms = (sorter: unknown, infos: ColumnInfo[], fields: Record<string, AnySqeField | undefined>): SqeQuery['sort'] => {
  const entries = (Array.isArray(sorter) ? sorter : [sorter]) as (SorterEntry | undefined)[];
  const terms = entries.flatMap(entry => {
    if (!entry?.order) return [];
    const info = infos.find(candidate => candidate.columnKey === String(entry.columnKey ?? entry.field));
    const spec = info ? fields[info.dataIndex] : undefined;
    if (!info || !spec || spec.kind === 'collection') return [];
    return [{ field: info.dataIndex, dir: entry.order === 'descend' ? ('desc' as const) : ('asc' as const) }];
  });
  return terms.length ? terms.slice(0, SQE_MAX_SORT_TERMS) : undefined;
};

/**
 * The SQE body for what the table asks for. Columns the list does not map to the SQE are left out. `defaultSort`
 * orders a request that names no sort, which the SQE would otherwise return in its tiebreak order.
 */
export const toSqeQuery = <T, N>(
  request: ServerSideRequest | undefined,
  columns: TableColumns<T>,
  sqeFields: SqeFields<N>,
  valueLists?: ValueLists,
  defaultSort?: SqeSort,
): SqeQuery => {
  const fields = sqeFields as Record<string, AnySqeField | undefined>;
  const infos = columnInfos(columns).filter(info => fields[info.dataIndex]);
  const filters = (request?.filters ?? {}) as Record<string, ColumnFilterEntry | undefined>;

  const nodes = infos.flatMap(info => {
    const entry = filters[info.columnKey];
    const node = entry ? columnFilter(info, fields[info.dataIndex] as AnySqeField, entry) : undefined;
    return node ? [node] : [];
  });

  const search = request?.query?.trim();
  if (search) {
    const anyColumn = group(
      'or',
      infos.flatMap(info => {
        const node = globalSearch(info, fields[info.dataIndex] as AnySqeField, search, valueLists);
        return node ? [node] : [];
      }),
    );
    if (anyColumn) nodes.push(anyColumn);
  }

  const pageSize = Math.min(request?.pagination?.pageSize ?? DEFAULT_PAGE_SIZE, SQE_MAX_PAGE_SIZE);
  return {
    filter: group('and', nodes),
    sort: sortTerms(request?.sorter, infos, fields) ?? defaultSort,
    page: request?.pagination?.current ?? 1,
    pageSize,
  };
};

/** The columns with each filter offering only the modes its SQE member can be queried with. */
export const withSqeFilterModes = <T, N>(columns: TableColumns<T>, sqeFields: SqeFields<N>): TableColumns<T> => {
  const fields = sqeFields as Record<string, AnySqeField | undefined>;
  return columns.map(column => {
    const { dataIndex, options } = column as { dataIndex?: unknown; options?: { filter?: string } };
    const spec = typeof dataIndex === 'string' ? fields[dataIndex] : undefined;
    const allowedModes = spec ? modesFor(spec, options?.filter) : undefined;
    if (!allowedModes) return column;
    return { ...column, options: { ...column.options, filterOptions: { ...column.options?.filterOptions, allowedModes } } };
  }) as TableColumns<T>;
};
